import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import RetryableError from '@shared/components/RetryableError';
import CardSection from '@shared/components/CardSection';
import { ApiRequestError } from '@api/client/apiClient';
import { useEcdisTargets } from '../hooks/useEcdisTargets';
import type { EcdisTarget } from '../types';

interface Props {
  selectedIds: string[];
  onToggle: (id: string) => void;
}

function formatLastConnected(iso?: string): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function TargetItem({
  target,
  checked,
  onToggle,
}: {
  target: EcdisTarget;
  checked: boolean;
  onToggle: () => void;
}) {
  const isDisconnected = target.status === 'disconnected';

  const label = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2">{target.name}</Typography>
      <Chip
        label={target.status}
        color={isDisconnected ? 'default' : 'success'}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
      <Typography variant="caption" color="text.secondary">
        {target.type} · Last: {formatLastConnected(target.lastConnectedAt)}
      </Typography>
    </Box>
  );

  const control = (
    <FormControlLabel
      control={
        <Checkbox size="small" checked={checked} onChange={onToggle} disabled={isDisconnected} />
      }
      label={label}
      sx={{ width: '100%', ml: 0 }}
    />
  );

  if (isDisconnected) {
    return (
      <Tooltip title="Target is disconnected and cannot receive transfers" placement="right">
        <Box sx={{ opacity: 0.6 }}>{control}</Box>
      </Tooltip>
    );
  }

  return control;
}

export default function EcdisTargetList({ selectedIds, onToggle }: Props) {
  const { data: targets, isLoading, error, refetch } = useEcdisTargets();

  if (isLoading) return <PanelSkeleton />;

  if (error) {
    const correlationId =
      error instanceof ApiRequestError ? error.apiError.correlationId : undefined;
    return (
      <RetryableError
        message={error.message ?? 'Failed to load ECDIS targets'}
        correlationId={correlationId}
        onRetry={() => void refetch()}
      />
    );
  }

  if (!targets || targets.length === 0) {
    return (
      <CardSection>
        <Typography variant="body2" color="text.secondary">
          No ECDIS targets available.
        </Typography>
      </CardSection>
    );
  }

  return (
    <div data-testid="ecdis-target-list">
      <CardSection title="Select ECDIS Targets">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {targets.map((target) => (
            <TargetItem
              key={target.ecdisId}
              target={target}
              checked={selectedIds.includes(target.ecdisId)}
              onToggle={() => onToggle(target.ecdisId)}
            />
          ))}
        </Box>
      </CardSection>
    </div>
  );
}
